import { useEffect, useState } from "react";
import { adminAPI } from "@/services/api";
import { formatCurrency } from "@/utils/constants";
import { toast } from "sonner";
import { Download, FileText, Landmark, TrendingUp, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CardGrid, LoadingState, PageHeader, PageShell, SectionCard, StatCard } from "@/components/app/page";

const AF_LABELS = {
  accompagnement_fondateur_edition: "Accompagnement edition",
  accompagnement_fondateur_parrainage: "Accompagnement parrainage",
  accompagnement_fondateur_equipe: "Accompagnement équipe",
};

export default function FounderDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await adminAPI.getFounderDashboard();
      setData(res.data);
    } catch (error) {
      toast.error("Erreur lors du chargement du tableau fondateur");
    } finally {
      setLoading(false);
    }
  };

  const downloadAdminAuditPdf = async () => {
    try {
      const res = await adminAPI.getAdminAuditPdf();
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `rapport_audits_admins_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Export PDF audit impossible");
    }
  };

  if (loading) return <LoadingState />;
  if (!data) return <LoadingState>Aucune donnee</LoadingState>;

  const cards = [
    { label: "Fondateur", value: data.founder_name || data.founder_id, icon: Landmark, tone: "pine" },
    { label: "Total accompagnement", value: formatCurrency(data.af_total_mc || 0), icon: TrendingUp, tone: "gold" },
    { label: "Ledger", value: formatCurrency(data.points_monnaie_ledger || 0), icon: Wallet, tone: "green" },
    { label: "Transactions", value: data.ledger_transaction_count || 0, icon: FileText, tone: "blue" },
  ];

  return (
    <PageShell data-testid="founder-dashboard">
      <PageHeader
        title="Tableau de bord Fondateur"
        description="Vue synthetique des accompagnements et mouvements fondateur."
        actions={
          <Button onClick={downloadAdminAuditPdf} className="bg-[#2A4B46] text-white hover:bg-[#1f3a36]">
            <Download size={16} className="mr-2" /> Rapport audits PDF
          </Button>
        }
      />

      <CardGrid>
        {cards.map((card) => (
          <StatCard key={card.label} title={card.label} value={card.value} icon={card.icon} tone={card.tone} />
        ))}
      </CardGrid>

      <div className="grid gap-6 lg:grid-cols-2">
        <SectionCard title="Accompagnements par type">
          <div className="space-y-3">
            {Object.entries(data.af_by_type || {}).map(([type, amount]) => (
              <div key={type} className="flex items-center justify-between gap-3 rounded-xl bg-[#F3EFE9] p-3">
                <span className="text-sm font-medium text-[#2A4B46]">{AF_LABELS[type] || type}</span>
                <span className="font-bold text-[#D4A373]">{formatCurrency(amount)}</span>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Evolution mensuelle">
          {data.af_by_month?.length ? (
            <div className="space-y-2">
              {data.af_by_month.map((row) => (
                <div key={row.month} className="flex items-center justify-between gap-3 border-b border-[#E6E4E0] py-2 last:border-b-0">
                  <span className="text-sm text-[#5C625F]">{row.month}</span>
                  <span className="font-semibold text-[#2A4B46]">{formatCurrency(row.total_mc)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-8 text-center text-[#5C625F]">Aucun mouvement</p>
          )}
        </SectionCard>
      </div>
    </PageShell>
  );
}
